import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import {
  DEFAULT_VIDEO_ANALYSIS_SETTINGS,
  VIDEO_ANALYSIS_POSE_FPS_OPTIONS,
  VIDEO_ANALYSIS_BALL_FPS_OPTIONS,
  VIDEO_ANALYSIS_POSE_PLAYER_OPTIONS,
  loadVideoAnalysisSettings,
  normalizeVideoAnalysisSettings,
  notifyVideoAnalysisSettingsChanged,
  saveVideoAnalysisSettings,
} from "../utils/videoAnalysisSettings";
import "./VideoAnalysisSettingsDialog.css";

/**
 * Coach settings for server-side video pose / ball analysis (sample rate, players, etc.).
 * Stored in localStorage and applied on the next Analyze / Pose / Ball run.
 */
export function VideoAnalysisSettingsDialog({
  open,
  onClose,
  t = (k, d) => d || k,
  onSaved,
}) {
  const [draft, setDraft] = useState(() => loadVideoAnalysisSettings());

  useEffect(() => {
    if (!open) return;
    setDraft(loadVideoAnalysisSettings());
  }, [open]);

  const poseFps = draft.poseFps;
  const ballDetectionFps = draft.ballDetectionFps;
  const poseDetectionPlayer = draft.poseDetectionPlayer;

  const handleSave = () => {
    const saved = saveVideoAnalysisSettings(draft);
    notifyVideoAnalysisSettingsChanged(saved);
    onSaved?.(saved);
    onClose?.();
  };

  const handleReset = () => {
    setDraft({ ...DEFAULT_VIDEO_ANALYSIS_SETTINGS });
  };

  const playerLabels = {
    A: t("poseDetectionPlayerA", "A (left / G)"),
    B: t("poseDetectionPlayerB", "B (right / D)"),
    Both: t("poseDetectionPlayerBoth", "Both"),
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      className="video-analysis-settings-dialog"
      aria-labelledby="video-analysis-settings-title"
      sx={{ zIndex: 13000 }}
    >
      <DialogTitle id="video-analysis-settings-title">
        {t("videoAnalysisSettings", "Video analysis settings")}
        <span className="video-analysis-settings-dialog__subtitle">
          {t(
            "videoAnalysisSettingsSubtitle",
            "Applies to Process session videos and the next pose / ball analysis on this device"
          )}
        </span>
      </DialogTitle>
      <DialogContent>
        <label
          className="video-analysis-settings-dialog__field"
          htmlFor="video-analysis-pose-fps"
        >
          <span className="video-analysis-settings-dialog__label">
            {t("poseSampleRate", "Pose sample rate")}
          </span>
          <span className="video-analysis-settings-dialog__hint">
            {t(
              "poseSampleRateHint",
              "How many pose detections per second of video. Higher = denser skeleton timeline, slower on CPU."
            )}
          </span>
          <div className="video-analysis-settings-dialog__fps-row">
            <select
              id="video-analysis-pose-fps"
              className="video-analysis-settings-dialog__select"
              value={String(poseFps)}
              onChange={(e) => {
                const v = Number(e.target.value);
                setDraft((prev) =>
                  normalizeVideoAnalysisSettings({ ...prev, poseFps: v })
                );
              }}
            >
              {VIDEO_ANALYSIS_POSE_FPS_OPTIONS.map((opt) => (
                <option key={opt} value={String(opt)}>
                  {opt} fps
                </option>
              ))}
              {!VIDEO_ANALYSIS_POSE_FPS_OPTIONS.includes(poseFps) ? (
                <option value={String(poseFps)}>{poseFps} fps</option>
              ) : null}
            </select>
            <input
              type="range"
              className="video-analysis-settings-dialog__range"
              min={0}
              max={VIDEO_ANALYSIS_POSE_FPS_OPTIONS.length - 1}
              step={1}
              value={Math.max(
                0,
                VIDEO_ANALYSIS_POSE_FPS_OPTIONS.indexOf(poseFps) >= 0
                  ? VIDEO_ANALYSIS_POSE_FPS_OPTIONS.indexOf(poseFps)
                  : VIDEO_ANALYSIS_POSE_FPS_OPTIONS.indexOf(30)
              )}
              onChange={(e) => {
                const idx = Number(e.target.value);
                const next =
                  VIDEO_ANALYSIS_POSE_FPS_OPTIONS[idx] ??
                  DEFAULT_VIDEO_ANALYSIS_SETTINGS.poseFps;
                setDraft((prev) =>
                  normalizeVideoAnalysisSettings({ ...prev, poseFps: next })
                );
              }}
              aria-label={t("poseSampleRate", "Pose sample rate")}
            />
          </div>
        </label>

        <label
          className="video-analysis-settings-dialog__field"
          htmlFor="video-analysis-ball-fps"
        >
          <span className="video-analysis-settings-dialog__label">
            {t("ballDetectionFps", "Ball detection rate")}
          </span>
          <span className="video-analysis-settings-dialog__hint">
            {t(
              "ballDetectionFpsHint",
              "How many ball detections per second of video. 120 uses every frame on club recordings (best speed/bounce fit). Default 60."
            )}
          </span>
          <div className="video-analysis-settings-dialog__fps-row">
            <select
              id="video-analysis-ball-fps"
              className="video-analysis-settings-dialog__select"
              value={String(ballDetectionFps)}
              onChange={(e) => {
                const v = Number(e.target.value);
                setDraft((prev) =>
                  normalizeVideoAnalysisSettings({
                    ...prev,
                    ballDetectionFps: v,
                  })
                );
              }}
            >
              {VIDEO_ANALYSIS_BALL_FPS_OPTIONS.map((opt) => (
                <option key={opt} value={String(opt)}>
                  {opt} fps
                </option>
              ))}
              {!VIDEO_ANALYSIS_BALL_FPS_OPTIONS.includes(ballDetectionFps) ? (
                <option value={String(ballDetectionFps)}>
                  {ballDetectionFps} fps
                </option>
              ) : null}
            </select>
            <input
              type="range"
              className="video-analysis-settings-dialog__range"
              min={0}
              max={VIDEO_ANALYSIS_BALL_FPS_OPTIONS.length - 1}
              step={1}
              value={Math.max(
                0,
                VIDEO_ANALYSIS_BALL_FPS_OPTIONS.indexOf(ballDetectionFps) >= 0
                  ? VIDEO_ANALYSIS_BALL_FPS_OPTIONS.indexOf(ballDetectionFps)
                  : VIDEO_ANALYSIS_BALL_FPS_OPTIONS.indexOf(60)
              )}
              onChange={(e) => {
                const idx = Number(e.target.value);
                const next =
                  VIDEO_ANALYSIS_BALL_FPS_OPTIONS[idx] ??
                  DEFAULT_VIDEO_ANALYSIS_SETTINGS.ballDetectionFps;
                setDraft((prev) =>
                  normalizeVideoAnalysisSettings({
                    ...prev,
                    ballDetectionFps: next,
                  })
                );
              }}
              aria-label={t("ballDetectionFps", "Ball detection rate")}
            />
          </div>
        </label>

        <fieldset className="video-analysis-settings-dialog__field video-analysis-settings-dialog__fieldset">
          <legend className="video-analysis-settings-dialog__label">
            {t("poseDetectionPlayer", "Pose detection player")}
          </legend>
          <span className="video-analysis-settings-dialog__hint">
            {t(
              "poseDetectionPlayerHint",
              "Which player(s) to run pose detection on. A is court-left (G), B is court-right (D)."
            )}
          </span>
          <div
            className="video-analysis-settings-dialog__segment"
            role="radiogroup"
            aria-label={t("poseDetectionPlayer", "Pose detection player")}
          >
            {VIDEO_ANALYSIS_POSE_PLAYER_OPTIONS.map((opt) => {
              const selected = poseDetectionPlayer === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={
                    selected
                      ? "video-analysis-settings-dialog__segment-btn is-selected"
                      : "video-analysis-settings-dialog__segment-btn"
                  }
                  onClick={() =>
                    setDraft((prev) =>
                      normalizeVideoAnalysisSettings({
                        ...prev,
                        poseDetectionPlayer: opt,
                      })
                    )
                  }
                >
                  {playerLabels[opt] || opt}
                </button>
              );
            })}
          </div>
        </fieldset>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} color="inherit">
          {t("resetDefaults", "Reset")}
        </Button>
        <Button onClick={onClose}>{t("cancel", "Cancel")}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t("save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VideoAnalysisSettingsDialog;
